import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useMates, useRemoveMate } from "@/hooks/useMates";
import { useQueryClient } from "@tanstack/react-query";
import { useMateLatestWorkout } from "@/hooks/useMateLatestWorkout";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { CustomLoader } from "@/components/ui/CustomLoader";
import { CustomLoadingError } from "@/components/ui/CustomLoadingError";
import { CustomNoData } from "@/components/ui/CustomNoData";
import { MateWorkoutCard } from "@/components/mates/MateWorkoutCard";
import PageTitleScript from "@/components/MetaScript";
import {
  ArrowLeft,
  Activity,
  UserMinus,
  MoreVertical,
  AlertTriangle,
} from "lucide-react";
import { displayRelativeTime } from "@/lib/dateUtils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToastHandler } from "@/hooks/useToastHandler";

export function MateProfilePage() {
  const { mateshipId } = useParams<{ mateshipId: string }>();
  const { data: mates, isLoading, error } = useMates();
  const {
    data: mateWorkoutData,
    isLoading: isWorkoutLoading,
    error: workoutError,
  } = useMateLatestWorkout(mateshipId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const removeMutation = useRemoveMate();
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const { handleSuccess } = useToastHandler();
  // 从伙伴列表中找到指定的伙伴
  // Find the specific mate from the mates list
  const mate = mates?.find((m) => m.mateship_id === mateshipId);

  // 在获取伙伴最新训练数据完成后，重新加载伙伴列表数据（包含待处理邀请）
  // 这样可以更新伙伴的"已读"状态，因为查看伙伴资料会自动标记为已读
  // Reload useMatesWithPending data after useMateLatestWorkout request is completed
  // This updates the mate's "read" status since viewing a mate's profile automatically marks it as read
  useEffect(() => {
    // 检查训练数据请求是否完成（成功获取数据或出现错误）
    // Check if workout data request is completed (either with data or error)
    if (!isWorkoutLoading && (mateWorkoutData || workoutError)) {
      // 立即重新获取伙伴列表数据，更新伙伴的"已读"状态，提高用户体验
      // Immediately refetch mates data to update the mate's "read" status for better UX
      queryClient.refetchQueries({ queryKey: ["mates", "with-pending"] });
    }
  }, [isWorkoutLoading, mateWorkoutData, workoutError, queryClient]);

  const getLastWorkoutText = () => {
    if (!mate?.mate_user.last_trained_at) {
      return "No workouts yet";
    }
    return (
      "Last workout: " + displayRelativeTime(mate.mate_user.last_trained_at)
    );
  };

  const handleRemoveClick = () => {
    setShowRemoveConfirmation(true);
  };

  const handleConfirmRemove = async () => {
    if (!mateshipId || !mate) return;

    try {
      await removeMutation.mutateAsync(mateshipId);
      setShowRemoveConfirmation(false);
      // Navigate back after successful removal
      // refetch mates data
      queryClient.refetchQueries({ queryKey: ["mates", "with-pending"] });
      handleSuccess("Mate removed successfully", 3000);
      // hard navigate to mates page
      setTimeout(() => {
        window.location.href = "/settings/mates";
      }, 1000);
    } catch (error) {
      console.error("Failed to remove mate:", error);
      setShowRemoveConfirmation(false);
      // Error handling could be improved with toast notifications
    }
  };

  if (isLoading) {
    return <CustomLoader />;
  }

  if (error) {
    return <CustomLoadingError />;
  }

  return (
    <div className="flex flex-col p-6 bg-gray-50 min-h-screen">
      <PageTitleScript title="Mate Profile" />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="mr-3 p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <h1 className="text-xl font-semibold text-gray-900">
            {mate?.mate_user.display_name || "Mate"}
          </h1>
        </div>

        {/* Ellipsis Menu */}
        {mate && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={handleRemoveClick}
                disabled={removeMutation.isPending}
                className="text-tx-error focus:text-tx-error"
              >
                <UserMinus className="mr-2 h-4 w-4" />
                Remove Mate
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {!mate ? (
        <CustomNoData msg="This mate could not be found." />
      ) : (
        <div className="max-w-md mx-auto w-full">
          {/* Mate Info Card - Similar to MateCardItem */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6 shadow-sm">
            <div className="flex items-center gap-3">
              {/* Avatar with unread badge */}
              <div className="relative flex-shrink-0">
                <Avatar className="h-16 w-16 border-2 border-gray-200">
                  <AvatarImage
                    src={mate.mate_user.avatar_url || undefined}
                    alt={`${mate.mate_user.display_name} avatar`}
                    loading="lazy"
                  />
                  <AvatarFallback className="text-lg font-medium bg-gray-100 text-gray-600">
                    N/A
                  </AvatarFallback>
                </Avatar>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-gray-900 text-lg truncate">
                  {mate.mate_user.display_name}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {getLastWorkoutText()}
                </p>
              </div>
            </div>
          </div>

          {/* Workout Content */}
          {isWorkoutLoading ? (
            <div className="flex justify-center py-8">
              <CustomLoader size="small" />
            </div>
          ) : workoutError ? (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <CustomLoadingError />
            </div>
          ) : !mateWorkoutData?.data.workouts.length ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <div className="text-center text-gray-500">
                <Activity className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium text-gray-700">
                  No workouts found
                </p>
                <p className="text-sm mt-1">
                  {mate.mate_user.display_name} hasn't logged any workouts yet.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {mateWorkoutData.data.workouts.map((workout) => (
                <MateWorkoutCard
                  key={workout.nano_id}
                  workout={workout}
                  sets={mateWorkoutData.data.sets}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Remove Mate Confirmation Modal */}
      <Dialog
        open={showRemoveConfirmation}
        onOpenChange={setShowRemoveConfirmation}
      >
        <DialogContent className="p-6 border-0 overflow-hidden max-w-md bg-white">
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Confirm
          </DialogTitle>
          <div className="border-t border-gray-200"></div>
          <DialogDescription className="text-tx-default text-base mb-5">
            Are you sure you want to remove{" "}
            <span className="font-medium">
              {mate?.mate_user.display_name || "this mate"}
            </span>{" "}
            from your mates list? This action cannot be undone and you will no
            longer be able to see each other's workouts.
          </DialogDescription>

          <div className="flex">
            <Button
              variant="destructive"
              size="lg"
              onClick={handleConfirmRemove}
              disabled={removeMutation.isPending}
              className="flex-1"
            >
              <UserMinus className="h-5 w-5 mr-2" />
              {removeMutation.isPending ? "Removing..." : "Remove Mate"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
